import { useState, useEffect, useCallback, useRef } from 'react';
import { db } from '../services/firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import { TIERS, FEATURES, TIER_FEATURES, LIMITS, isPaidTier } from '../constants/subscriptionLimits';

const USAGE_KEY = 'adhd-planner-usage-counters';

function getTodayKey() {
  return new Date().toISOString().split('T')[0]; // YYYY-MM-DD
}

function getWeekKey() {
  const d = new Date();
  const jan1 = new Date(d.getFullYear(), 0, 1);
  const week = Math.ceil(((d - jan1) / 86400000 + jan1.getDay() + 1) / 7);
  return `${d.getFullYear()}-W${week}`;
}

function loadCounters() {
  try {
    return JSON.parse(localStorage.getItem(USAGE_KEY) || '{}');
  } catch {
    return {};
  }
}

function saveCounters(c) {
  localStorage.setItem(USAGE_KEY, JSON.stringify(c));
}

/**
 * useSubscription — Hook principal pour la gestion des abonnements
 *
 * Retourne :
 *   isPro          — boolean : l'utilisateur a un abonnement actif
 *   tier           — 'free' | 'pro_monthly' | 'pro_annual'
 *   status         — 'active' | 'trialing' | 'cancelled' | 'past_due' | null
 *   loading        — boolean
 *   subscription   — objet complet depuis Firestore
 *   checkFeature   — (featureName) => boolean
 *   checkLimit     — (limitName) => { allowed: boolean, current: number, max: number }
 *   incrementUsage — (limitName) => void  (incrémente le compteur de l'utilisateur)
 */
export function useSubscription(userId) {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const countersRef = useRef(loadCounters());

  useEffect(() => {
    if (!userId || !db) {
      setLoading(false);
      return;
    }

    const subRef = doc(db, 'users', userId, 'subscription', 'current');
    const unsub = onSnapshot(
      subRef,
      (snap) => {
        if (snap.exists()) {
          setSubscription(snap.data());
        } else {
          setSubscription({ tier: TIERS.FREE, status: null });
        }
        setLoading(false);
      },
      (err) => {
        console.warn('[useSubscription] Firestore error:', err.message);
        setSubscription({ tier: TIERS.FREE, status: null });
        setLoading(false);
      }
    );

    return unsub;
  }, [userId]);

  const tier = subscription?.tier || TIERS.FREE;
  const status = subscription?.status || null;
  const isPro = isPaidTier(tier) && (status === 'active' || status === 'trialing');

  /* Check if a named feature is available */
  const checkFeature = useCallback((featureName) => {
    if (isPro) return true;
    return TIER_FEATURES[TIERS.FREE]?.has(featureName) ?? false;
  }, [isPro]);

  /* Check a usage-based limit and whether the user is within it */
  const checkLimit = useCallback((limitName) => {
    if (isPro) return { allowed: true, current: 0, max: Infinity };

    const limits = LIMITS.free;
    const max = limits[limitName] ?? Infinity;
    if (max === Infinity) return { allowed: true, current: 0, max: Infinity };

    const counters = countersRef.current;
    let key;
    let current;

    if (limitName === 'aiGenerationsPerWeek') {
      key = `${limitName}_${getWeekKey()}`;
    } else {
      key = `${limitName}_${getTodayKey()}`;
    }

    current = counters[key] || 0;
    return { allowed: current < max, current, max };
  }, [isPro]);

  /* Increment a usage counter */
  const incrementUsage = useCallback((limitName) => {
    if (isPro) return;
    const counters = countersRef.current;
    let key;

    if (limitName === 'aiGenerationsPerWeek') {
      key = `${limitName}_${getWeekKey()}`;
    } else {
      key = `${limitName}_${getTodayKey()}`;
    }

    counters[key] = (counters[key] || 0) + 1;
    countersRef.current = counters;
    saveCounters(counters);
  }, [isPro]);

  return {
    subscription,
    tier,
    status,
    isPro,
    loading,
    checkFeature,
    checkLimit,
    incrementUsage,
    FEATURES,
    TIERS,
  };
}
